import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Button,
  Text,
  useToast,
  SimpleGrid,
  VStack,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Container,
  Checkbox,
  CheckboxGroup,
  Wrap,
  WrapItem,
  Badge,
  Input
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { applyStrategy, getStrategyList } from '../services/apiService'
import { useAuthState } from '../utils/authUtils'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'

interface DynamicItem {
  roi: number
  size: number
}

interface Strategy {
  id: number
  name: string
  description: string
  leverage: number
  tp: number
  sl: number
  size: number
  dynamic: DynamicItem[]
  divideSeed: number
}

const Strategy = () => {
  const { t } = useTranslation()
  const { reloadUser } = useAuthState()
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const toast = useToast()
  const navigate = useNavigate()
  const {
    isOpen: isDynamicOpen,
    onOpen: onDynamicOpen,
    onClose: onDynamicClose
  } = useDisclosure()

  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(
    null
  )
  const [auth] = useRecoilState(authState)
  const [applying, setApplying] = useState(false)

  // 시드 & 심볼 선택 모달
  const {
    isOpen: isApplyOpen,
    onOpen: onApplyOpen,
    onClose: onApplyClose
  } = useDisclosure()
  const [selectedApplyStrategy, setSelectedApplyStrategy] =
    useState<Strategy | null>(null)
  const [seedAmount, setSeedAmount] = useState<string>('')

  // ✅ 선택된 심볼들
  const availableSymbols = useMemo(
    () =>
      Array.isArray(auth?.trading) ? auth.trading.map((t) => t.symbol) : [],
    [auth?.trading]
  )
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([])

  // 심볼별 봇 상태 조회 유틸
  const getBotStatusBySymbol = (symbol: string): boolean => {
    if (!Array.isArray(auth?.trading)) return false
    const item = auth.trading.find((t) => t.symbol === symbol)
    return Boolean(item?.botStatus)
  }

  // 전략 적용 버튼
  const handleApply = (strategy: Strategy) => {
    setSelectedApplyStrategy(strategy)
    setSeedAmount('')
    // 기본값: 모든 사용 가능한 심볼 중 봇이 꺼져 있는 심볼만 선선택
    const initial = availableSymbols.filter((sym) => !getBotStatusBySymbol(sym))
    setSelectedSymbols(initial)
    onApplyOpen()
  }

  // 실제 적용 로직 실행
  const handleConfirmApply = async () => {
    if (!selectedApplyStrategy) return

    if (!seedAmount || isNaN(Number(seedAmount))) {
      toast({
        title: t('전략 적용'),
        description: t('유효한 투자 시드를 입력해주세요.'),
        status: 'info',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (!selectedSymbols.length) {
      toast({
        title: t('전략 적용'),
        description: t('적용할 심볼을 1개 이상 선택해주세요.'),
        status: 'info',
        duration: 2500,
        isClosable: true
      })
      return
    }

    // 실행 중인 심볼 체크 (막기)
    const running = selectedSymbols.filter((sym) => getBotStatusBySymbol(sym))
    if (running.length > 0) {
      toast({
        title: t('전략 적용 불가'),
        description: t(
          '다음 심볼은 봇이 실행 중입니다: {{symbols}}\\n봇 종료 후 다시 시도해주세요.',
          { symbols: running.join(', ') }
        ),
        status: 'warning',
        duration: 3500,
        isClosable: true
      })
      return
    }

    try {
      setApplying(true)

      const { data: res } = await applyStrategy({
        id: Number(selectedApplyStrategy.id),
        seed: Number(seedAmount),
        symbols: selectedSymbols // ✅ 선택된 심볼 배열 전달
      })

      if (res.statusCode === 200) {
        toast({
          title: t('전략 적용'),
          description: t(
            '"{{name}}" 전략이 {{symbols}} 심볼에 {{seed}} USDT로 적용되었습니다.',
            {
              name: selectedApplyStrategy.name,
              symbols: selectedSymbols.join(', '),
              seed: seedAmount
            }
          ),
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        onApplyClose()
        reloadUser()
      }
    } catch (error: any) {
      toast({
        title: t('전략 적용'),
        description:
          error?.response?.data?.message || t('전략 적용에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setApplying(false)
    }
  }

  const fetchStrategies = async () => {
    try {
      const { data: res } = await getStrategyList()
      if (res.statusCode === 200) {
        setStrategies(res.data)
      }
    } catch (error) {
      toast({
        title: t('전략 로드 실패'),
        description: t('전략 목록을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const handleShowDynamic = (strategy: Strategy) => {
    setSelectedStrategy(strategy)
    onDynamicOpen()
  }

  useEffect(() => {
    fetchStrategies()
  }, [])

  return (
    <DefaultLayout>
      <Container maxW="container.xl" p={0} mt={4}>
        <Box px={8} py={4}>
          {/* 제목 및 설명 */}
          <VStack align="start" spacing={1} mb={6}>
            <Text fontSize="2xl" fontWeight="bold">
              {t('전략 라이브러리')}
            </Text>
            <Text fontSize="md" color="gray.600">
              {t('원하는 전략을 선택하여 적용할 수 있습니다.')}
            </Text>
          </VStack>

          {/* 보유 심볼 상태 섹션 */}
          {Array.isArray(auth?.trading) && auth.trading.length > 0 && (
            <Box mb={6} p={4} borderWidth="1px" borderRadius="md">
              <Text fontWeight="bold" mb={2}>
                {t('내 보유 심볼 상태')}
              </Text>
              <Wrap>
                {auth.trading.map((t) => (
                  <WrapItem key={t.symbol}>
                    <Badge
                      colorScheme={t.botStatus ? 'green' : 'gray'}
                      variant="solid"
                      px={3}
                      py={1}
                      borderRadius="md"
                    >
                      {t.symbol} • {t.botStatus ? 'RUNNING' : 'STOPPED'}
                    </Badge>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}

          {/* 전략 목록 */}
          {strategies.length === 0 ? (
            <Text textAlign="center" color="gray.500">
              {t('등록된 전략이 없습니다.')}
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {strategies.map((strategy) => (
                <Box
                  key={strategy.id}
                  borderWidth="1px"
                  borderRadius="lg"
                  p={4}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  minHeight="360px"
                >
                  {/* 내용 영역 */}
                  <VStack align="start" spacing={2} flex="1">
                    <Text fontSize="lg" fontWeight="bold">
                      {strategy.name}
                    </Text>
                    <Box
                      color="gray.600"
                      dangerouslySetInnerHTML={{ __html: strategy.description }}
                    />
                    <Divider my={3} />
                    <Text>
                      {t('레버리지: {{n}}배', { n: strategy.leverage })}
                    </Text>
                    <Text>{t('익절: {{n}}%', { n: strategy.tp })}</Text>
                    <Text>
                      {t('손절: {{n}}%', {
                        n: strategy.sl > 0 ? strategy.sl : t('손절 미적용')
                      })}
                    </Text>
                    <Text>{t('사이즈: {{n}}', { n: strategy.size })}</Text>
                    <Text>
                      {t('물타기 단계 수: {{n}}', {
                        n: strategy.dynamic?.length ?? 0
                      })}
                    </Text>
                    <Text>
                      {t('* 투자 시드의 {{n}}%를 사용합니다.', {
                        n: strategy.divideSeed
                      })}
                    </Text>
                  </VStack>

                  {/* 버튼 영역 */}
                  <VStack mt={4} spacing={2}>
                    {auth.id && (
                      <Button
                        colorScheme="purple"
                        size="sm"
                        onClick={() => handleApply(strategy)}
                        w="100%"
                      >
                        {t('전략 적용')}
                      </Button>
                    )}

                    <Button
                      colorScheme="gray"
                      size="sm"
                      onClick={() => handleShowDynamic(strategy)}
                      w="100%"
                    >
                      {t('물타기 상세 보기')}
                    </Button>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </Box>

        {/* 물타기 상세 모달 */}
        <Modal
          isOpen={isDynamicOpen}
          onClose={onDynamicClose}
          isCentered
          size="lg"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {t('{{name}} - 물타기 상세', {
                name: selectedStrategy?.name ?? ''
              })}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedStrategy?.dynamic &&
              selectedStrategy.dynamic.length > 0 ? (
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>#</Th>
                      <Th>ROI (%)</Th>
                      <Th>Size</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {selectedStrategy.dynamic.map((item, index) => (
                      <Tr key={index}>
                        <Td>{index + 1}</Td>
                        <Td>{item.roi}</Td>
                        <Td>{item.size}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Text>{t('물타기 정보가 없습니다.')}</Text>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={onDynamicClose}>{t('닫기')}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* 전략 적용(심볼 선택 + 시드 입력) 모달 */}
        <Modal isOpen={isApplyOpen} onClose={onApplyClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('전략 적용')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <HStack spacing={1}>
                    <Text color="purple" fontWeight="bold">
                      {selectedApplyStrategy?.name}
                    </Text>
                    <Text>{t('전략을 선택하셨습니다.')}</Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    {t('적용할 심볼을 선택하고, 투자 시드를 입력하세요.')}
                  </Text>
                </Box>

                {/* 심볼 선택 */}
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    {t('심볼 선택')}
                  </Text>
                  {availableSymbols.length === 0 ? (
                    <Text color="gray.500">
                      {t('사용 가능한 심볼이 없습니다.')}
                    </Text>
                  ) : (
                    <CheckboxGroup
                      colorScheme="purple"
                      value={selectedSymbols}
                      onChange={(vals) => setSelectedSymbols(vals as string[])}
                    >
                      <Wrap>
                        {availableSymbols.map((sym) => {
                          const running = getBotStatusBySymbol(sym)
                          return (
                            <WrapItem key={sym}>
                              <Checkbox value={sym} isDisabled={running}>
                                <HStack spacing={2}>
                                  <Text>{sym}</Text>
                                  <Badge
                                    colorScheme={running ? 'green' : 'gray'}
                                  >
                                    {t(running ? 'RUNNING' : 'STOPPED')}
                                  </Badge>
                                </HStack>
                              </Checkbox>
                            </WrapItem>
                          )
                        })}
                      </Wrap>
                    </CheckboxGroup>
                  )}
                </Box>

                {/* 시드 입력 */}
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    {t('투자 시드 (USDT)')}
                  </Text>
                  <Input
                    type="number"
                    value={seedAmount}
                    onChange={(e) => setSeedAmount(e.target.value)}
                    placeholder="100"
                  />
                  <Text mt={2} fontSize="sm" color="gray.600">
                    {t(
                      '* 선택한 심볼에 대해 시드 분할({{n}}%)과 사이즈 계산이 자동 적용됩니다.',
                      { n: selectedApplyStrategy?.divideSeed ?? 0 }
                    )}
                  </Text>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onApplyClose}>
                {t('취소')}
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleConfirmApply}
                isLoading={applying}
              >
                {t('적용')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Box mb={10} />
      </Container>

      <Box mb={10} />
    </DefaultLayout>
  )
}

export default Strategy
